import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, Check, Layers, Package, Link2 } from "lucide-react";
import { CYCLE, TONES } from "@/data/content";
import { GoldButton } from "@/components/Section";

const RADIUS = 92;
const CIRC = 2 * Math.PI * RADIUS;

const Radial = ({ active, onSelect }) => (
  <div className="relative mx-auto w-[260px] h-[260px] lg:w-[300px] lg:h-[300px]" data-testid="methodology-radial">
    <svg viewBox="0 0 240 240" className="absolute inset-0 w-full h-full -rotate-90">
      <circle cx="120" cy="120" r={RADIUS} fill="none" stroke="#0E1B31" strokeOpacity="0.08" strokeWidth="14" />
      {CYCLE.map((c, i) => {
        const seg = CIRC / 6;
        const on = i <= active;
        return (
          <circle
            key={c.step}
            cx="120"
            cy="120"
            r={RADIUS}
            fill="none"
            stroke={TONES[c.tone].accent}
            strokeWidth={i === active ? 16 : 12}
            strokeOpacity={on ? 1 : 0.18}
            strokeDasharray={`${seg - 6} ${CIRC - seg + 6}`}
            strokeDashoffset={-i * seg}
            strokeLinecap="butt"
            style={{ transition: "stroke-opacity .6s, stroke-width .4s" }}
          />
        );
      })}
    </svg>
    {CYCLE.map((c, i) => {
      const a = (i / 6) * Math.PI * 2 - Math.PI / 2 + Math.PI / 6;
      const x = 50 + Math.cos(a) * 38.5;
      const y = 50 + Math.sin(a) * 38.5;
      const isActive = i === active;
      return (
        <button
          key={c.step}
          data-testid={`radial-step-${c.step}`}
          onClick={() => onSelect(i)}
          className={`absolute -translate-x-1/2 -translate-y-1/2 flex h-9 w-9 items-center justify-center rounded-full text-[10px] font-mono-brand transition-all duration-500 ${isActive ? "text-white scale-110 pulse-ring" : "bg-white text-[#0E1B31] border border-[#0E1B31]/10 hover:scale-105"}`}
          style={{ left: `${x}%`, top: `${y}%`, background: isActive ? TONES[c.tone].accent : undefined, color: isActive ? "#fff" : undefined }}
          aria-label={c.title}
        >
          {c.step}
        </button>
      );
    })}
    <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center text-center px-10">
      <AnimatePresence mode="wait">
        <motion.div key={active} initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 1.05 }} transition={{ duration: 0.4 }}>
          <p className="font-mono-brand text-[10px] tracking-[0.3em] uppercase" style={{ color: TONES[CYCLE[active].tone].accent }}>Στάδιο {CYCLE[active].step}</p>
          <p className="mt-1 font-display text-2xl lg:text-3xl font-semibold text-[#0E1B31]">{CYCLE[active].title}</p>
        </motion.div>
      </AnimatePresence>
    </div>
  </div>
);

const Timeline = ({ active, onSelect }) => (
  <div className="relative" data-testid="methodology-timeline">
    <div className="absolute left-0 right-0 top-[22px] h-px bg-[#0E1B31]/10" />
    <motion.div
      className="absolute left-0 top-[22px] h-px bg-gradient-to-r from-[#C26D52] via-[#2C4A6F] to-[#C5A059]"
      animate={{ width: `${(active / (CYCLE.length - 1)) * 100}%` }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
    />
    <div className="relative grid grid-cols-3 sm:grid-cols-6 gap-y-6">
      {CYCLE.map((c, i) => {
        const isActive = i === active;
        const t = TONES[c.tone];
        return (
          <button
            key={c.step}
            data-testid={`cycle-step-${c.step}`}
            onClick={() => onSelect(i)}
            className="group flex min-w-0 flex-col items-center gap-3 text-center"
          >
            <span
              className={`relative flex h-11 w-11 items-center justify-center rounded-full border text-[11px] font-mono-brand transition-all duration-500 ${isActive ? "text-white border-transparent scale-110 shadow-lg" : "bg-white text-[#0E1B31] border-[#0E1B31]/10 group-hover:border-[#0E1B31]/40"}`}
              style={{ background: isActive ? t.accent : undefined }}
            >
              {isActive && <span className="pulse-ring absolute inset-0 rounded-full" style={{ color: t.accent }} />}
              {c.step}
            </span>
            <span className={`w-full min-w-0 break-words font-display text-base sm:text-lg font-semibold leading-tight transition-colors ${isActive ? "text-[#0E1B31]" : "text-[#6B7A8F] group-hover:text-[#0E1B31]"}`}>{c.title}</span>
          </button>
        );
      })}
    </div>
  </div>
);

const Block = ({ icon: Icon, label, tone, children }) => (
  <div className="rounded-2xl border border-[#0E1B31]/[0.07] bg-white/80 p-5">
    <p className="flex items-center gap-2 font-mono-brand text-[10px] tracking-[0.25em] uppercase text-[#6B7A8F]">
      <Icon className="h-3.5 w-3.5" style={{ color: tone }} /> {label}
    </p>
    <div className="mt-3">{children}</div>
  </div>
);

export const MethodologyExperience = ({ compact = false }) => {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const current = CYCLE[active];
  const t = TONES[current.tone];
  const next = CYCLE[(active + 1) % CYCLE.length];

  useEffect(() => {
    if (paused) return;
    const id = setInterval(() => setActive((a) => (a + 1) % CYCLE.length), 6500);
    return () => clearInterval(id);
  }, [paused]);

  const select = (i) => {
    setPaused(true);
    setActive(i);
  };

  return (
    <div data-testid="philosophy-cycle" className="relative">
      <div className={`grid grid-cols-1 ${compact ? "lg:grid-cols-12" : "lg:grid-cols-12"} gap-10 lg:gap-14 items-start`}>
        <div className={compact ? "lg:col-span-4" : "lg:col-span-4"}>
          <Radial active={active} onSelect={select} />
          <p className="mt-4 text-center text-xs text-[#6B7A8F]">Επιλέξτε ένα στάδιο για να δείτε τι περιλαμβάνει.</p>
        </div>

        <div className={compact ? "lg:col-span-8" : "lg:col-span-8"}>
          <Timeline active={active} onSelect={select} />

          <AnimatePresence mode="wait">
            <motion.div
              key={current.step}
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              data-testid="cycle-detail-panel"
              className="relative mt-10 overflow-hidden rounded-[30px] border border-[#0E1B31]/[0.07] p-7 lg:p-10 shadow-[0_30px_70px_-45px_rgba(14,27,49,0.4)]"
              style={{ background: `linear-gradient(135deg, #ffffff 0%, ${t.tint} 100%)` }}
            >
              <span className="pointer-events-none absolute -right-4 -top-10 font-display text-[11rem] leading-none font-semibold select-none" style={{ color: `${t.accent}14` }}>
                {current.step}
              </span>
              <div className="relative">
                <p className="font-mono-brand text-[11px] tracking-[0.3em] uppercase" style={{ color: t.accent }}>Στάδιο {current.step} / 06</p>
                <h3 className="mt-3 font-display text-3xl sm:text-4xl font-semibold text-[#0E1B31]">{current.title}</h3>
                <p className="mt-4 text-base leading-relaxed text-[#3B4A5E] max-w-2xl">{compact ? current.desc : current.does}</p>

                {!compact && (
                  <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Block icon={Layers} label="Τι αναλύεται / δημιουργείται" tone={t.accent}>
                      <ul className="space-y-2">
                        {current.creates.map((d) => (
                          <li key={d} className="flex items-start gap-2.5 text-sm text-[#3B4A5E]">
                            <span className="mt-2 h-1.5 w-1.5 rounded-full shrink-0" style={{ background: t.accent }} />
                            {d}
                          </li>
                        ))}
                      </ul>
                    </Block>
                    <Block icon={Package} label="Τι παραλαμβάνετε" tone={t.accent}>
                      <ul className="space-y-2">
                        {current.receives.map((d) => (
                          <li key={d} className="flex items-start gap-2.5 text-sm text-[#3B4A5E]">
                            <Check className="h-4 w-4 mt-0.5 shrink-0" style={{ color: t.accent }} />
                            {d}
                          </li>
                        ))}
                      </ul>
                    </Block>
                  </div>
                )}

                {compact && (
                  <ul className="mt-6 flex flex-wrap gap-2">
                    {current.receives.map((d) => (
                      <li key={d} className="rounded-full border bg-white/80 px-3.5 py-1.5 text-[12px] text-[#3B4A5E]" style={{ borderColor: `${t.accent}44` }}>{d}</li>
                    ))}
                  </ul>
                )}

                <button
                  data-testid="cycle-next-button"
                  onClick={() => select((active + 1) % CYCLE.length)}
                  className="group mt-8 flex w-full flex-col items-start justify-between gap-4 rounded-2xl bg-[#0E1B31] px-5 py-4 text-left text-white transition-colors hover:bg-[#16294A] sm:flex-row sm:items-center"
                >
                  <span className="flex min-w-0 items-start gap-3">
                    <Link2 className="h-4 w-4 mt-0.5 shrink-0 text-[#C5A059]" />
                    <span className="min-w-0">
                      <span className="block font-mono-brand text-[10px] tracking-[0.25em] uppercase text-white/60">Σύνδεση με το επόμενο στάδιο</span>
                      <span className="mt-1 block text-sm text-white/85">{current.next}</span>
                    </span>
                  </span>
                  <span className="flex max-w-full items-center gap-2 self-end text-right text-xs font-semibold text-[#C5A059] sm:shrink-0 sm:self-auto">
                    {next.step} {next.title}
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </span>
                </button>
              </div>
            </motion.div>
          </AnimatePresence>

          {compact && (
            <div className="mt-8">
              <GoldButton to="/approach" testId="methodology-preview-link" variant="outline">Δείτε τη μεθοδολογία αναλυτικά</GoldButton>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
